import React from 'react';

const WordCloudModal = ({ 
  isOpen, 
  onClose, 
  props, 
  onWordClick 
}) => {
  if (!isOpen) return null;

  // Generate Word Cloud Data
  const generateWordCloudData = () => {
    const tagCounts = {};
    const categoryCounts = {};
    const materialCounts = {};
    const periodCounts = {};
    
    // Count occurrences across all props
    props.forEach(prop => {
      // Count categories
      if (prop.category) {
        categoryCounts[prop.category] = (categoryCounts[prop.category] || 0) + 1;
      }
      
      // Count tags
      if (prop.tags && Array.isArray(prop.tags)) {
        prop.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
      
      // Count materials
      if (prop.material && Array.isArray(prop.material)) {
        prop.material.forEach(material => {
          materialCounts[material] = (materialCounts[material] || 0) + 1;
        });
      }
      
      // Count historical periods
      if (prop.historical_period) {
        periodCounts[prop.historical_period] = (periodCounts[prop.historical_period] || 0) + 1;
      }
    });
    
    // Combine all counts into word cloud items
    const wordCloudItems = [];
    
    // Add categories (largest)
    Object.entries(categoryCounts).forEach(([word, count]) => {
      wordCloudItems.push({
        text: word,
        count,
        type: 'category',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 hover:bg-blue-200',
        size: Math.min(Math.max(count * 8, 16), 32) // 16px to 32px
      });
    });
    
    // Add popular tags (medium)
    Object.entries(tagCounts)
      .filter(([word, count]) => count >= 2) // Only show tags with 2+ occurrences
      .forEach(([word, count]) => {
        wordCloudItems.push({
          text: word,
          count,
          type: 'tag',
          color: 'text-green-600',
          bgColor: 'bg-green-100 hover:bg-green-200',
          size: Math.min(Math.max(count * 6, 14), 24) // 14px to 24px
        });
      });
    
    // Add materials (smaller)
    Object.entries(materialCounts)
      .filter(([word, count]) => count >= 2)
      .forEach(([word, count]) => {
        wordCloudItems.push({
          text: word,
          count,
          type: 'material',
          color: 'text-purple-600',
          bgColor: 'bg-purple-100 hover:bg-purple-200',
          size: Math.min(Math.max(count * 4, 12), 20) // 12px to 20px
        });
      });
    
    // Add periods (medium)
    Object.entries(periodCounts).forEach(([word, count]) => {
      wordCloudItems.push({
        text: word,
        count,
        type: 'period',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 hover:bg-orange-200',
        size: Math.min(Math.max(count * 5, 14), 26) // 14px to 26px
      });
    });
    
    // Sort by count (descending) and shuffle similar counts for variety
    return wordCloudItems
      .sort((a, b) => b.count - a.count)
      .slice(0, 50) // Limit to 50 items for performance
      .sort(() => Math.random() - 0.5); // Shuffle for visual variety
  };

  const handleWordClick = (wordItem) => {
    onWordClick(wordItem);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2">☁️ Category & Tag Cloud</h2>
              <p className="text-blue-100">Click any word to filter props. Size indicates frequency across all props.</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Word Cloud Content */}
        <div className="p-8 overflow-y-auto max-h-[75vh]">
          {/* Legend */}
          <div className="mb-6 flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-200 rounded"></span>
              <span className="text-sm text-gray-600">Categories</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-200 rounded"></span>
              <span className="text-sm text-gray-600">Tags</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-purple-200 rounded"></span>
              <span className="text-sm text-gray-600">Materials</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-orange-200 rounded"></span>
              <span className="text-sm text-gray-600">Historical Periods</span>
            </div>
          </div>
          
          {/* Word Cloud Container */}
          <div className="min-h-[400px] relative">
            <div className="flex flex-wrap justify-center items-center gap-2 p-4">
              {generateWordCloudData().map((wordItem, index) => (
                <button
                  key={`${wordItem.text}-${index}`}
                  onClick={() => handleWordClick(wordItem)}
                  className={`
                    ${wordItem.bgColor} ${wordItem.color}
                    px-3 py-2 rounded-full border border-gray-200
                    transition-all duration-200 hover:scale-110 hover:shadow-md
                    font-medium cursor-pointer
                  `}
                  style={{
                    fontSize: `${wordItem.size}px`,
                    lineHeight: '1.2',
                    transform: `rotate(${Math.random() * 10 - 5}deg)` // Slight random rotation
                  }}
                  title={`${wordItem.text} (${wordItem.count} props) - Click to filter`}
                >
                  {wordItem.text}
                  <span className="ml-1 text-xs opacity-75">({wordItem.count})</span>
                </button>
              ))}
            </div>
            
            {/* Empty state */}
            {generateWordCloudData().length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <div className="text-4xl mb-4">☁️</div>
                  <p className="text-lg font-medium mb-2">No data for word cloud</p>
                  <p className="text-sm">Add more props with tags and categories to see the cloud</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Usage Tips */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">💡 How to use the word cloud:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Larger words</strong> appear more frequently across props</li>
              <li>• <strong>Blue words</strong> are categories - click to filter by category</li>
              <li>• <strong>Green/Purple/Orange words</strong> are tags, materials, or periods - click to search</li>
              <li>• Numbers in parentheses show how many props contain that word</li>
              <li>• Hover over words to see details</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordCloudModal;